import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { User, getAuth, signOut } from 'firebase/auth';
import { getFirebaseApp } from '../core/firebase';
import { AuthBridgeService } from '../core/services/auth-bridge.service';
import { OrgContextService } from '../core/services/org-context.service';

// Refresh the cached `emailVerified` flag from Firebase (e.g. user clicked the
// verification link in another tab) before deciding whether to bounce them.
//
// Microsoft's OIDC sign-in doesn't surface an `email_verified` claim the way
// Google does, so Firebase leaves `emailVerified=false` after a successful
// signInWithPopup with microsoft.com — even though Microsoft has fully
// authenticated the user and asserted their email. Treat a linked
// microsoft.com provider as equivalent verification.
async function isEmailVerified(user: User): Promise<boolean> {
  if (user.emailVerified) return true;
  if (user.providerData.some((p) => p.providerId === 'microsoft.com')) return true;
  try {
    await user.reload();
  } catch {
    // Don't lock the user out on a transient network failure.
    return true;
  }
  return user.emailVerified;
}

// Plan's limited-release allowlist: Crewdle staff only. `endsWith('@crewdle.com')`
// is safe against look-alikes (e.g. `x@notcrewdle.com` doesn't match) since the
// `@` anchors the domain boundary.
function isCrewdleEmail(email: string | null | undefined): boolean {
  return !!email && email.toLowerCase().endsWith('@crewdle.com');
}

/**
 * Guard for the `/login` route. Unauthenticated users are handed off to the
 * centralized auth portal; authenticated users (including a session just
 * established by exchanging an `?code=` callback after re-login) are redirected
 * into the app. Without this, landing the OAuth callback on `/login` would
 * activate the route and leave the user stuck on the signing-out loader.
 */
export const redirectLoggedInTo = (target: string) => {
  const guard: CanActivateFn = async () => {
    const bridge = inject(AuthBridgeService).bridge;
    const router = inject(Router);

    let user: User | null;
    try {
      user = await bridge.ensureSession();
    } catch {
      user = null;
    }

    if (!user) {
      await bridge.login();
      return false;
    }

    // Authenticated — leave the login route for the app (drops any ?code= params).
    return router.parseUrl(target);
  };
  return guard;
};

export const redirectUnauthorizedTo = (_redirect: string) => {
  return (() => {
    const guard: CanActivateFn = async (route, state) => {
      const bridge = inject(AuthBridgeService).bridge;
      const orgContext = inject(OrgContextService);
      const router = inject(Router);

      // Resolve a local session, or exchange an authorization-code callback.
      // Works the same on localhost (against staging auth) and deployed origins.
      let user: User | null;
      try {
        user = await bridge.ensureSession();
      } catch {
        user = null;
      }

      // No session → start the authorization-code flow at auth.crewdle.ai.
      if (!user) {
        await bridge.login();
        return false;
      }

      if (!(await isEmailVerified(user))) {
        await signOut(getAuth(getFirebaseApp())).catch(() => {});
        await bridge.disconnectRegions();
        await bridge.login();
        return false;
      }

      // Plan is in limited release — restricted to Crewdle staff (@crewdle.com)
      // for now. Non-Crewdle accounts are bounced to a friendly page instead of
      // loading the app and hitting permission-denied (server-side enforcement
      // is `isPlanAllowed()` in firestore.rules). Drop this and the rule to open
      // Plan up to all orgs.
      if (!isCrewdleEmail(user.email)) {
        return router.parseUrl('/unavailable');
      }

      // After a fresh code-exchange login the session is established here (late),
      // so OrgContext's bootstrap init() ran without a user. Load orgs now —
      // before the requireOrg child guard runs — so it doesn't briefly bounce
      // to /new while org data is still loading.
      if (!orgContext.currentOrg()) {
        await orgContext.init().catch(() => undefined);
      }

      // Strip the OAuth callback params from the URL. The bridge clears them via
      // history.replaceState, but Angular Router re-applies this navigation's
      // query params when it finalizes — so redirect to the clean path instead.
      if (route.queryParamMap.has('code')) {
        return router.parseUrl(state.url.split('?')[0]);
      }

      return true;
    };
    return guard;
  })();
};
